import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FaFolderOpen, FaVideo, FaRobot, FaCar, FaClock, FaChartBar, FaChartLine, FaUsers, FaStar } from 'react-icons/fa';

const UserActivityTab = ({ casefiles, userEvidence, allEvidence }) => {
  // Calculate comprehensive stats with weekly trends
  const stats = useMemo(() => {
    const totalCases = casefiles.length;
    const publicCases = casefiles.filter(file => file.isPublic).length;
    const evidenceUploaded = userEvidence.length;
    
    // Parse AI analysis results
    let totalVehiclesDetected = 0;
    let totalPlatesDetected = 0;
    let successfulAnalyses = 0;
    
    userEvidence.forEach(evidence => {
      if (evidence.analysis) {
        successfulAnalyses++;
        try {
          const analysis = JSON.parse(evidence.analysis);
          if (analysis.vehicles && Array.isArray(analysis.vehicles)) {
            totalVehiclesDetected += analysis.vehicles.length;
            analysis.vehicles.forEach(vehicle => {
              if (vehicle.license_plate && vehicle.license_plate !== 'Unknown') {
                totalPlatesDetected++;
              }
            });
          }
        } catch (error) {
          // If JSON parsing fails, try to extract from text
          const plateMatches = evidence.analysis.match(/license plate|plate number/gi);
          if (plateMatches && plateMatches.length > 0) {
            totalPlatesDetected += plateMatches.length;
          }
        }
      }
    });
    
    const analysisRate = evidenceUploaded > 0 ? Math.round((successfulAnalyses / evidenceUploaded) * 100) : 0;
    const vehicleDetectionRate = successfulAnalyses > 0 ? Math.round((totalVehiclesDetected / successfulAnalyses) * 100) : 0;
    
    // NEW: Weekly Activity Trends
    const now = new Date();
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const twoWeeksAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
    
    const thisWeekEvidence = userEvidence.filter(evidence => 
      evidence.createdAt && evidence.createdAt.toDate() > oneWeekAgo
    ).length;
    
    const lastWeekEvidence = userEvidence.filter(evidence => 
      evidence.createdAt && 
      evidence.createdAt.toDate() <= oneWeekAgo && 
      evidence.createdAt.toDate() > twoWeeksAgo
    ).length;
    
    const weeklyGrowth = lastWeekEvidence > 0 ? 
      Math.round(((thisWeekEvidence - lastWeekEvidence) / lastWeekEvidence) * 100) : 
      (thisWeekEvidence > 0 ? 100 : 0);
    
    // NEW: Success Rate Improvements
    const recentAnalyses = userEvidence.filter(evidence => 
      evidence.analysisTimestamp && 
      evidence.analysisTimestamp.toDate() > oneWeekAgo
    );
    
    const recentSuccessRate = recentAnalyses.length > 0 ? 
      Math.round((recentAnalyses.filter(e => e.analysis).length / recentAnalyses.length) * 100) : 0;
    
    // NEW: User Engagement Metrics
    const activeDays = new Set();
    userEvidence.forEach(evidence => {
      if (evidence.createdAt) {
        const date = evidence.createdAt.toDate().toDateString();
        activeDays.add(date);
      }
    });
    
    const engagementScore = Math.min(100, Math.round((activeDays.size / 7) * 100));
    const isActiveUser = activeDays.size >= 3;
    
    // NEW: Community Contribution Score
    const communityScore = Math.min(100, Math.round(
      (publicCases * 20) + 
      (evidenceUploaded * 5) + 
      (successfulAnalyses * 10)
    ));
    
    return {
      totalCases,
      publicCases,
      evidenceUploaded,
      successfulAnalyses,
      totalVehiclesDetected,
      totalPlatesDetected,
      analysisRate,
      vehicleDetectionRate,
      // NEW: Enhanced metrics
      thisWeekEvidence,
      weeklyGrowth,
      recentSuccessRate,
      engagementScore,
      communityScore,
      isActiveUser,
      activeDays: activeDays.size
    };
  }, [casefiles, userEvidence]);

  // Generate recent activity items
  const recentActivity = useMemo(() => {
    const activities = [];
    
    // Recent casefiles (last 30 days)
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    casefiles
      .filter(file => file.createdAt && file.createdAt.toDate() > thirtyDaysAgo)
      .sort((a, b) => b.createdAt.toDate() - a.createdAt.toDate())
      .slice(0, 5)
      .forEach(file => {
        activities.push({
          type: 'casefile',
          title: `Created case: ${file.title}`,
          date: file.createdAt.toDate(),
          link: `/casefile/${file.id}`,
          icon: FaFolderOpen,
          color: '#76a9ea'
        });
      });
    
    // Recent evidence uploads (last 30 days)
    userEvidence
      .filter(evidence => evidence.createdAt && evidence.createdAt.toDate() > thirtyDaysAgo)
      .sort((a, b) => b.createdAt.toDate() - a.createdAt.toDate())
      .slice(0, 5)
      .forEach(evidence => {
        const casefile = casefiles.find(c => c.id === evidence.casefileId);
        activities.push({
          type: 'evidence',
          title: `Uploaded evidence: ${evidence.fileName}`,
          subtitle: casefile ? `to ${casefile.title}` : '',
          date: evidence.createdAt.toDate(),
          link: `/casefile/${evidence.casefileId}`,
          icon: FaVideo,
          color: '#28a745'
        });
      });
    
    // Recent AI analyses (last 30 days)
    userEvidence
      .filter(evidence => 
        evidence.analysisTimestamp && 
        evidence.analysisTimestamp.toDate() > thirtyDaysAgo
      )
      .sort((a, b) => b.analysisTimestamp.toDate() - a.analysisTimestamp.toDate())
      .slice(0, 5)
      .forEach(evidence => {
        const casefile = casefiles.find(c => c.id === evidence.casefileId);
        activities.push({
          type: 'analysis',
          title: `AI analysis completed: ${evidence.fileName}`,
          subtitle: casefile ? `in ${casefile.title}` : '',
          date: evidence.analysisTimestamp.toDate(),
          link: `/casefile/${evidence.casefileId}`,
          icon: FaRobot,
          color: '#ffc107'
        });
      });
    
    // Sort all activities by date and return top 10
    return activities
      .sort((a, b) => b.date - a.date)
      .slice(0, 10);
  }, [casefiles, userEvidence]);

  const formatTimeAgo = (date) => {
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="tab-content user-activity-tab">
      <div className="tab-header">
        <h2>Your Activity & Stats</h2>
      </div>

      {/* Enhanced Statistics Grid */}
      <div className="stats-grid">
        <div className="stat-box">
          <FaFolderOpen className="stat-icon" />
          <div className="stat-content">
            <h3>{stats.totalCases}</h3>
            <p>Total Cases</p>
            {stats.publicCases > 0 && (
              <span className="stat-detail">{stats.publicCases} public</span>
            )}
          </div>
        </div>

        <div className="stat-box">
          <FaVideo className="stat-icon" />
          <div className="stat-content">
            <h3>{stats.evidenceUploaded}</h3>
            <p>Evidence Uploaded</p>
            {stats.successfulAnalyses > 0 && (
              <span className="stat-detail">{stats.successfulAnalyses} analyzed</span>
            )}
          </div>
        </div>

        <div className="stat-box">
          <FaCar className="stat-icon" />
          <div className="stat-content">
            <h3>{stats.totalVehiclesDetected}</h3>
            <p>Vehicles Found</p>
            {stats.totalPlatesDetected > 0 && (
              <span className="stat-detail">{stats.totalPlatesDetected} plates</span>
            )}
          </div>
        </div>

        <div className="stat-box">
          <FaRobot className="stat-icon" />
          <div className="stat-content">
            <h3>{stats.analysisRate}%</h3>
            <p>Analysis Rate</p>
            {stats.vehicleDetectionRate > 0 && (
              <span className="stat-detail">{stats.vehicleDetectionRate}% success</span>
            )}
          </div>
        </div>
      </div>

      {/* NEW: Weekly Trends Section */}
      <div className="trends-section">
        <h3>📈 Weekly Trends</h3>
        <div className="trends-grid">
          <div className="trend-card">
            <div className="trend-header">
              <FaChartBar className="trend-icon" />
              <span>This Week's Activity</span>
            </div>
            <div className="trend-content">
              <h4>{stats.thisWeekEvidence}</h4>
              <p>Evidence Uploaded</p>
              <div className={`trend-indicator ${stats.weeklyGrowth >= 0 ? 'positive' : 'negative'}`}>
                {stats.weeklyGrowth >= 0 ? '↗' : '↘'} {Math.abs(stats.weeklyGrowth)}%
              </div>
            </div>
          </div>

          <div className="trend-card">
            <div className="trend-header">
              <FaChartLine className="trend-icon" />
              <span>Recent Success Rate</span>
            </div>
            <div className="trend-content">
              <h4>{stats.recentSuccessRate}%</h4>
              <p>Analysis Success</p>
              <div className="trend-indicator positive">
                {stats.recentSuccessRate >= 80 ? '🎯' : stats.recentSuccessRate >= 60 ? '👍' : '📈'}
              </div>
            </div>
          </div>

          <div className="trend-card">
            <div className="trend-header">
              <FaUsers className="trend-icon" />
              <span>Engagement Score</span>
            </div>
            <div className="trend-content">
              <h4>{stats.engagementScore}%</h4>
              <p>Active Days: {stats.activeDays}/7</p>
              <div className={`trend-indicator ${stats.isActiveUser ? 'positive' : 'neutral'}`}>
                {stats.isActiveUser ? '🔥 Active' : '💤 Inactive'}
              </div>
            </div>
          </div>

          <div className="trend-card">
            <div className="trend-header">
              <FaStar className="trend-icon" />
              <span>Community Score</span>
            </div>
            <div className="trend-content">
              <h4>{stats.communityScore}</h4>
              <p>Contribution Points</p>
              <div className="trend-indicator positive">
                {stats.communityScore >= 80 ? '🌟 Elite' : stats.communityScore >= 50 ? '⭐ Rising' : '🌱 New'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Quick Insights */}
      {stats.totalCases > 0 && (
        <div className="insights-section">
          <h3>📊 Quick Insights</h3>
          <div className="insights-content">
            {stats.thisWeekEvidence > stats.lastWeekEvidence && (
              <p>🚀 Great momentum! You uploaded {stats.thisWeekEvidence} pieces of evidence this week - {stats.weeklyGrowth}% increase from last week!</p>
            )}
            {stats.recentSuccessRate >= 90 && (
              <p>🎯 Outstanding! Your recent analysis success rate is {stats.recentSuccessRate}% - you're becoming an AI analysis expert!</p>
            )}
            {stats.isActiveUser && (
              <p>🔥 You're an active user! Engaged for {stats.activeDays} days this week. Keep up the great work!</p>
            )}
            {stats.communityScore >= 100 && (
              <p>🌟 Elite contributor! Your community score of {stats.communityScore} shows you're making a real impact.</p>
            )}
            {stats.totalVehiclesDetected >= 10 && (
              <p>🚗 Excellent! AI found {stats.totalVehiclesDetected} vehicles across your evidence. Share findings with law enforcement!</p>
            )}
            {stats.analysisRate >= 80 && stats.evidenceUploaded >= 5 && (
              <p>🤖 Great analysis coverage! {stats.analysisRate}% of your evidence has been processed by AI.</p>
            )}
            {stats.publicCases === 0 && stats.totalCases >= 3 && (
              <p>🌐 Consider making some cases public for collaboration and community help.</p>
            )}
            {stats.evidenceUploaded === 0 && stats.totalCases > 0 && (
              <p>📹 Upload video evidence to your cases to get started with AI analysis.</p>
            )}
          </div>
        </div>
      )}

      {/* Recent Activity Feed */}
      <div className="activity-section">
        <h3>🕒 Recent Activity</h3>
        {recentActivity.length === 0 ? (
          <div className="empty-activity">
            <p>No recent activity to show.</p>
            <p>Create cases and upload evidence to see your activity here!</p>
          </div>
        ) : (
          <ul className="activity-feed">
            {recentActivity.map((activity, index) => {
              const IconComponent = activity.icon;
              return (
                <li key={index} className="activity-item">
                  <div className="activity-icon" style={{ color: '#E53935' }}>
                    <IconComponent />
                  </div>
                  <div className="activity-content">
                    <Link to={activity.link} className="activity-title">
                      {activity.title}
                    </Link>
                    {activity.subtitle && (
                      <p className="activity-subtitle">{activity.subtitle}</p>
                    )}
                    <span className="activity-time">
                      <FaClock /> {formatTimeAgo(activity.date)}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default UserActivityTab; 